/** Section 27 - war machines, and the rule that lets a gate be barred.
 *
 * A war machine is an ordinary `CreatureDef` carrying `SIEGE_MACHINE`. It is
 * fielded by a hero rather than recruited, stands on a hex it never leaves,
 * takes no morale, never retaliates, and is not folded back into the army when
 * the battle ends (`slotInArmy` is -1 for every machine stack).
 *
 * **What a machine does is read off the fields every creature already has**,
 * not off its id and not off an ability minted per machine. Two facts decide
 * it - whether the machine has a bow, and whether it has a bracket of damage:
 *
 *   |             | damage > 0            | damage == 0                  |
 *   | bow         | artillery  (ballista) | quartermaster   (ammo cart)  |
 *   | no bow      | breaker    (catapult) | medic     (first aid tent)   |
 *
 * The point of the table is that adding a fifth machine is a content edit. The
 * point of *breaker* in particular is the siege: a town's gate can only be
 * barred while somebody on the field can open it, so `startBattle` bars the
 * gate exactly when the attacking hero brings a breaker and leaves it standing
 * open otherwise. A gate no one can break is not a hard siege, it is a battle
 * that cannot be concluded.
 */

import type { CreatureDefId } from "../ids.js";
import type { ContentRegistry, CreatureDef } from "../content/registry.js";
import type { StatKey } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { FP_ONE, clampInt } from "../numeric.js";
import {
  F_MACHINE_AMMO, F_MACHINE_BREAK, F_MACHINE_HEAL, F_SHOOTER, F_WAR_MACHINE,
  type StackStore, isAlive,
} from "./state.js";

export type MachineRole = "none" | "artillery" | "quartermaster" | "breaker" | "medic";

/** The role table above, applied to one def. */
export function machineRoleOf(def: CreatureDef): MachineRole {
  let machine = false;
  let bow = false;
  for (const a of def.abilities) {
    if (a.kind === "SIEGE_MACHINE") machine = true;
    else if (a.kind === "SHOOTER") bow = true;
  }
  if (!machine) return "none";
  const damage = def.damageMax > 0;
  if (bow) return damage ? "artillery" : "quartermaster";
  return damage ? "breaker" : "medic";
}

/** The role bits for a def, folded into `flagsForCreature`'s result. */
export function machineFlagsOf(def: CreatureDef): number {
  switch (machineRoleOf(def)) {
    case "breaker": return F_MACHINE_BREAK;
    case "medic": return F_MACHINE_HEAL;
    case "quartermaster": return F_MACHINE_AMMO;
    case "artillery": case "none": return 0;
  }
}

export const isWarMachine = (st: StackStore, i: number): boolean =>
  (st.flags[i]! & F_WAR_MACHINE) !== 0;

/**
 * The catapult is the one stack on the field that cannot be shot at. The classic makes
 * it untouchable, and the rule is load-bearing here rather than decorative: it
 * is what guarantees that a barred gate always comes down, so an assault the
 * attacker can start is an assault he can finish.
 *
 * It is the SIEGE ENGINE that is untouchable, not everything that can hit a
 * wall. A gun that also batters (the Cannon, through `WALL_BREAKER`) is a gun
 * standing on the field and may be shot like any other - otherwise buying one
 * would hand Cogworks an invulnerable shooter, which is not what the counter is
 * selling. "A breaker with no bow" is the whole of the test.
 */
export const isUntargetable = (st: StackStore, i: number): boolean =>
  (st.flags[i]! & F_MACHINE_BREAK) !== 0 && (st.flags[i]! & F_SHOOTER) === 0;

/** May this stack aim at masonry? The catapult by its shape, the Cannon by its
 *  `WALL_BREAKER` ability; both carry the same bit. */
export const canBatter = (st: StackStore, i: number): boolean =>
  (st.flags[i]! & F_MACHINE_BREAK) !== 0;

export const canTend = (st: StackStore, i: number): boolean =>
  (st.flags[i]! & F_MACHINE_HEAL) !== 0;

/** Quartermasters never fire; their whole shot goes to the rest of the army. */
export const canShoot = (st: StackStore, i: number): boolean =>
  (st.flags[i]! & F_SHOOTER) !== 0 && (st.flags[i]! & F_MACHINE_AMMO) === 0;

/** Section 27 - shooters on a side with a standing ammo cart never run dry. */
export function sideHasAmmoCart(st: StackStore, side: number): boolean {
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] !== side || !isAlive(st, i)) continue;
    if ((st.flags[i]! & F_MACHINE_AMMO) !== 0) return true;
  }
  return false;
}

export function sideHasBreaker(st: StackStore, side: number): boolean {
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] !== side || !isAlive(st, i)) continue;
    if ((st.flags[i]! & F_MACHINE_BREAK) !== 0) return true;
  }
  return false;
}

/**
 * The siege engine every besieging hero brings. The classic does not let a hero leave
 * the catapult at home, so the kernel supplies it rather than making the
 * adventure layer remember; *which* engine that is, content decides - the
 * lowest-id breaker it ships. Sorted, so two runs pick the same one.
 */
export function defaultSiegeEngine(content: ContentRegistry): CreatureDefId | null {
  let best: CreatureDefId | null = null;
  for (const def of content.creatures.values()) {
    if (machineRoleOf(def) !== "breaker") continue;
    if (best === null || (def.id as string) < (best as string)) best = def.id;
  }
  return best;
}

// ---------------------------------------------------------------------- crew
//
// The classic hands a machine to the player only when he has trained for it: Ballistics
// aims the breaker, Artillery works the guns, First Aid runs the tent. An
// untrained machine acts on its own, which is what `execute.ts` calls the
// uncrewed rule and what kept the two machine commands unreachable until now.
//
// The crew level reaches the battle kernel the way Offense and school mastery
// already do - as a Modifier on the stack, so it hashes, journals and replays -
// and it is read back off the machine's own slot rather than off the hero,
// because the machine is the thing the rule is about.

export const STAT_CREW_ARTILLERY = "machine.artillery" as StatKey;
export const STAT_CREW_BALLISTICS = "machine.ballistics" as StatKey;
export const STAT_CREW_FIRST_AID = "machine.first_aid" as StatKey;

/**
 * The role a stack's flags say it is. `none` for anything that is not a machine.
 *
 * A bow settles it before masonry does. The Cannon carries the breaker bit AND
 * a bow, and it is a gun: it is crewed by Artillery, it spends its turn
 * shooting, and battering is the second thing it can do. Reading the breaker
 * bit first - which this did while the catapult was the only thing that had it
 * - would have handed the Cannon to Ballistics and left Artillery, the skill
 * its own description names, crewing nothing.
 */
export function roleOfFlags(flags: number): MachineRole {
  if ((flags & F_WAR_MACHINE) === 0) return "none";
  if ((flags & F_MACHINE_HEAL) !== 0) return "medic";
  if ((flags & F_MACHINE_AMMO) !== 0) return "quartermaster";
  if ((flags & F_SHOOTER) !== 0) return "artillery";
  if ((flags & F_MACHINE_BREAK) !== 0) return "breaker";
  return "artillery";
}

/** Which skill crews which role. A quartermaster needs none: it never acts. */
export function crewStatOf(role: MachineRole): StatKey | null {
  switch (role) {
    case "breaker": return STAT_CREW_BALLISTICS;
    case "artillery": return STAT_CREW_ARTILLERY;
    case "medic": return STAT_CREW_FIRST_AID;
    case "quartermaster": case "none": return null;
  }
}

/** 0..3: how well this machine's owner is trained to work it. */
export function crewLevel(st: StackStore, slot: number): number {
  const stat = crewStatOf(roleOfFlags(st.flags[slot]!));
  if (stat === null) return 0;
  const mods = st.mods[slot]!.filter((m) => m.stat === stat);
  if (mods.length === 0) return 0;
  return clampInt(resolveStat(0, mods).value, 0, 3);
}

/** A crewed machine is commanded; an uncrewed one is resolved by the kernel. */
export const isCrewed = (st: StackStore, slot: number): boolean => crewLevel(st, slot) > 0;

/**
 * What training buys, indexed by skill level 0..3. Index 0 is the uncrewed
 * machine, so a table's first entry is always "unchanged".
 *
 *  - Artillery multiplies the gun's damage bracket and, at expert, fires it a
 *    second time in the same action. The multiplier is installed as a Modifier
 *    at battle start; the second shot is an `AttackShape`, the same mechanism
 *    DOUBLE_ATTACK uses.
 *  - Ballistics is measured in masonry rather than damage: how many segments
 *    one volley reaches, and how deep each hit bites. A wall segment has two
 *    points of integrity, so expert Ballistics fells a section per hit.
 *  - First Aid is a flat pool of hit points, which is how the classic prices the tent:
 *    25 untrained, 100 at expert, plus the hero's spell power either way.
 */
export const ARTILLERY_DAMAGE_FP: readonly number[] = Object.freeze([FP_ONE, 1500, 2000, 2000]);
export const ARTILLERY_STRIKES: readonly number[] = Object.freeze([1, 1, 1, 2]);
export const BALLISTICS_HITS: readonly number[] = Object.freeze([1, 1, 2, 2]);
export const BALLISTICS_BITE: readonly number[] = Object.freeze([1, 1, 1, 2]);
export const FIRST_AID_HEAL: readonly number[] = Object.freeze([25, 50, 75, 100]);

/** Extra strikes an artillery crew gets in one action. */
export function artilleryStrikes(st: StackStore, slot: number): number {
  if (roleOfFlags(st.flags[slot]!) !== "artillery") return 1;
  return ARTILLERY_STRIKES[crewLevel(st, slot)] ?? 1;
}

/**
 * The training a volley at masonry is measured with.
 *
 * The siege engine's own crew, and NOBODY ELSE'S: a gun that batters does it at
 * the untrained rung whatever its crew knows. Two reasons, and the second is
 * the one that matters. Ballistics is what the tables above are indexed by, and
 * a Cannon is crewed by Artillery - indexing a masonry table with a gunnery
 * level is a category error that would read as a number either way. And a
 * Cannon that felled a section a turn would be a catapult you can also shoot
 * the army with, which makes the engine every besieger already brings for
 * nothing pointless. So the gun chips: one segment, one point of integrity,
 * and the wall still comes down faster when the catapult is doing the work.
 */
export function batterCrewLevel(st: StackStore, slot: number): number {
  return roleOfFlags(st.flags[slot]!) === "breaker" ? crewLevel(st, slot) : 0;
}
